
import React from 'react';
import { Check } from 'lucide-react';
import AnimatedNumber from './AnimatedNumber';
import { OfferDetails, formatPrice } from '@/utils/calculations';
import ContactInfo from './contact/ContactInfo';

interface OfferComparisonProps {
  standardPrice: number;
  premiumPrice: number;
  exclusivePrice: number;
  standardDetails: OfferDetails;
  premiumDetails: OfferDetails;
  exclusiveDetails: OfferDetails;
  selectedOffer: 'standard' | 'premium' | 'exclusive' | null;
  onSelectOffer: (offer: 'standard' | 'premium' | 'exclusive') => void;
}

const OfferComparison: React.FC<OfferComparisonProps> = ({
  standardPrice,
  premiumPrice,
  exclusivePrice,
  standardDetails,
  premiumDetails,
  exclusiveDetails,
  selectedOffer,
  onSelectOffer
}) => {
  return (
    <>
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6 w-full max-w-6xl mx-auto">
        {/* Standard Offer */}
        <div 
          className={`glass-card p-6 transition-all duration-300 ${
            selectedOffer === 'standard' 
              ? 'ring-2 ring-chillwise scale-[1.02]' 
              : 'hover:scale-[1.01] cursor-pointer'
          }`}
          onClick={() => onSelectOffer('standard')}
        >
          <div className="space-y-4">
            <div className="inline-block px-3 py-1 rounded-full bg-gray-100 text-sm font-medium text-gray-800 mb-2">
              Standard
            </div>
            <div className="text-3xl font-bold flex items-end">
              <AnimatedNumber 
                value={standardPrice} 
                formatter={formatPrice} 
                className="text-4xl"
              />
              <span className="text-gray-500 text-lg ml-1 mb-1">PLN</span>
            </div>

            <div className="h-px w-full bg-gray-100 my-4"></div>

            <div className="space-y-3">
              <p className="text-gray-500 font-medium">Zawiera:</p>
              <ul className="space-y-2">
                {standardDetails.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-chillwise mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div>
                <p className="text-gray-500">Gwarancja</p>
                <p className="font-medium">{standardDetails.warranty}</p>
              </div>
              <div>
                <p className="text-gray-500">Czas instalacji</p>
                <p className="font-medium">{standardDetails.installationTime}</p>
              </div>
            </div>

            <button 
              className={`mt-4 w-full py-3 rounded-xl font-medium transition-all ${
                selectedOffer === 'standard'
                  ? 'bg-chillwise text-white'
                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectOffer('standard');
              }}
            >
              {selectedOffer === 'standard' ? 'Wybrano' : 'Wybierz Standard'}
            </button>
          </div>
        </div>

        {/* Premium Offer */}
        <div 
          className={`premium-card p-6 transition-all duration-300 ${
            selectedOffer === 'premium' 
              ? 'ring-2 ring-chillwise scale-[1.02]' 
              : 'hover:scale-[1.01] cursor-pointer'
          }`}
          onClick={() => onSelectOffer('premium')}
        >
          <div className="space-y-4">
            <div className="inline-block px-3 py-1 rounded-full bg-chillwise/10 text-sm font-medium text-chillwise mb-2">
              Premium
            </div>
            <div className="text-3xl font-bold flex items-end">
              <AnimatedNumber 
                value={premiumPrice} 
                formatter={formatPrice}
                className="text-4xl"
              />
              <span className="text-gray-500 text-lg ml-1 mb-1">PLN</span>
            </div>

            <div className="h-px w-full bg-gray-100 my-4"></div>

            <div className="space-y-3">
              <p className="text-gray-500 font-medium">Zawiera:</p>
              <ul className="space-y-2">
                {premiumDetails.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-chillwise mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div>
                <p className="text-gray-500">Gwarancja</p>
                <p className="font-medium">{premiumDetails.warranty}</p>
              </div>
              <div>
                <p className="text-gray-500">Czas instalacji</p>
                <p className="font-medium">{premiumDetails.installationTime}</p>
              </div>
            </div>

            <button 
              className={`mt-4 w-full py-3 rounded-xl font-medium transition-all ${
                selectedOffer === 'premium'
                  ? 'bg-chillwise text-white'
                  : 'bg-chillwise/10 text-chillwise hover:bg-chillwise/20'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectOffer('premium');
              }}
            >
              {selectedOffer === 'premium' ? 'Wybrano' : 'Wybierz Premium'}
            </button>
          </div>
        </div>

        {/* Exclusive Offer */}
        <div 
          className={`exclusive-card p-6 transition-all duration-300 ${
            selectedOffer === 'exclusive' 
              ? 'ring-2 ring-chillwise scale-[1.02]' 
              : 'hover:scale-[1.01] cursor-pointer'
          }`}
          onClick={() => onSelectOffer('exclusive')}
        >
          <div className="space-y-4">
            <div className="inline-block px-3 py-1 rounded-full bg-chillwise-dark/10 text-sm font-medium text-chillwise-dark mb-2">
              Exclusive
            </div>
            <div className="text-3xl font-bold flex items-end">
              <AnimatedNumber 
                value={exclusivePrice} 
                formatter={formatPrice}
                className="text-4xl"
              />
              <span className="text-gray-500 text-lg ml-1 mb-1">PLN</span>
            </div>

            <div className="h-px w-full bg-gray-100 my-4"></div>

            <div className="space-y-3">
              <p className="text-gray-500 font-medium">Zawiera:</p>
              <ul className="space-y-2">
                {exclusiveDetails.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 text-chillwise-dark mr-2 mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
              <div>
                <p className="text-gray-500">Gwarancja</p>
                <p className="font-medium">{exclusiveDetails.warranty}</p>
              </div>
              <div>
                <p className="text-gray-500">Czas instalacji</p>
                <p className="font-medium">{exclusiveDetails.installationTime}</p>
              </div>
            </div>

            <button 
              className={`mt-4 w-full py-3 rounded-xl font-medium transition-all ${
                selectedOffer === 'exclusive'
                  ? 'bg-chillwise-dark text-white'
                  : 'bg-chillwise-dark/10 text-chillwise-dark hover:bg-chillwise-dark/20'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onSelectOffer('exclusive');
              }}
            >
              {selectedOffer === 'exclusive' ? 'Wybrano' : 'Wybierz Exclusive'}
            </button>
          </div>
        </div>
      </div>
      
      {/* Contact Information */}
      <div className="w-full max-w-6xl mx-auto">
        <ContactInfo />
      </div>
    </>
  );
};

export default OfferComparison;
